import { useState, type FormEvent } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/lib/auth-context";
import { BlobBackground, GlassCard } from "@/components/ui";

// ════════════════════════════════════════════════════════════════════════════
// Login Page
// ════════════════════════════════════════════════════════════════════════════

// Virtual receptionists land on the VR workspace; everyone else on /dashboard
function homePathFor(user: { role: string; isPlatformUser?: boolean } | null): string {
  return user?.isPlatformUser && user.role === "virtual_receptionist" ? "/vr" : "/dashboard";
}

export default function Login() {
  const { login, isAuthenticated, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tenantSlug, setTenantSlug] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Redirect if already logged in
  if (isAuthenticated) {
    window.location.href = homePathFor(user);
    return null;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      // Pass tenant slug if provided (for tenant login)
      const loggedInUser = await login(email, password, tenantSlug || undefined);
      window.location.href = homePathFor(loggedInUser);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      {/* Animated Blob Background */}
      <BlobBackground />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-af-900 dark:bg-af-100 mb-4">
            <span className="text-2xl font-bold text-white dark:text-af-900">AF</span>
          </div>
          <h1 className="text-af-900 dark:text-af-100">Appointment Flo</h1>
          <p className="text-af-700 dark:text-af-300 mt-2">Sign in to your account</p>
        </div>

        {/* Login Form */}
        <GlassCard className="p-8">
          <form onSubmit={handleSubmit}>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-600 dark:text-red-400 text-sm"
              >
                {error}
              </motion.div>
            )}

            <div className="mb-4">
              <label
                htmlFor="tenantSlug"
                className="block text-label text-af-700 dark:text-af-300 mb-2"
              >
                Business ID <span className="text-xs text-af-500">(leave empty for platform admin)</span>
              </label>
              <input
                id="tenantSlug"
                type="text"
                value={tenantSlug}
                onChange={(e) => setTenantSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                className="glass-input w-full"
                placeholder="e.g., test-medspa"
                disabled={isLoading}
              />
            </div>

            <div className="mb-4">
              <label
                htmlFor="email"
                className="block text-label text-af-700 dark:text-af-300 mb-2"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="glass-input w-full"
                placeholder="you@example.com"
                required
                disabled={isLoading}
              />
            </div>

            <div className="mb-6">
              <label
                htmlFor="password"
                className="block text-label text-af-700 dark:text-af-300 mb-2"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="glass-input w-full"
                placeholder="Enter your password"
                required
                disabled={isLoading}
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="glass-button-primary w-full py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="animate-spin h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Signing in...
                </span>
              ) : (
                "Sign in"
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <a
              href="/forgot-password"
              className="text-sm text-af-500 hover:text-af-700 dark:hover:text-af-300 transition-colors"
            >
              Forgot your password?
            </a>
          </div>
        </GlassCard>

        <p className="mt-8 text-center text-sm text-af-500">
          Powered by{" "}
          <a
            href="https://appointmentflo.com"
            className="font-medium text-af-700 dark:text-af-300 hover:text-af-900 dark:hover:text-af-100 transition-colors"
          >
            AppointmentFlo.com
          </a>
        </p>
      </motion.div>
    </div>
  );
}
